<template>
  <node-view-wrapper class="signature-node-wrapper">
    <!-- Edit mode: placeholder -->
    <div v-if="isEditMode" class="signature-pending">
      <span class="signature-placeholder-text">
        {{
          $t("Место для подписи") +
          ": " +
          (node.attrs.participant_name || $t("Участник"))
        }}
      </span>
      <button class="signature-delete-btn" @click="deleteNode">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <!-- SIGNED: EDS (ЭЦП) — 6 QR chunks -->
    <div v-else-if="isSigned && isEDS" class="signature-signed">
      <div class="signature-qr-row">
        <qrcode-vue
          v-for="(chunk, i) in qrChunks"
          :key="i"
          :value="chunk"
          :size="100"
          render-as="svg"
          level="L"
        />
      </div>
      <span class="signature-label">
        [{{
          isNotary
            ? $t("Электронная подпись нотариуса")
            : $t("Электронная подпись")
        }}
        {{ signatureLabel }}]
      </span>
    </div>

    <!-- SIGNED: Handwritten — images in row -->
    <div v-else-if="isSigned && !isEDS" class="signature-signed">
      <div v-if="signatureFileUrl" class="signature-hand-box">
        <img
          :src="signatureFileUrl"
          alt="signature"
          class="signature-hand-img"
          :class="{ 'signature-hand-img--rotated': isVertical }"
          @load="onImageLoad"
        />
      </div>
      <span class="signature-label">
        [{{ $t("Подпись рукой") }} {{ signatureLabel }}]
      </span>
    </div>

    <!-- Preview/View: no signature or not signed yet -->
    <div v-else class="signature-pending">
      <span>
        {{
          $t("Ожидание подписи") +
          ": " +
          (node.attrs.participant_name || $t("Участник"))
        }}
      </span>
    </div>
  </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper } from "@tiptap/vue-3";
import QrcodeVue from "qrcode.vue";
import { SignatureStatus } from "@/shared/config/signatureStatuses";

const QR_COUNT = 6;

export default {
  name: "SignatureNodeView",
  components: {
    NodeViewWrapper,
    QrcodeVue,
  },
  props: {
    node: {
      type: Object,
      required: true,
    },
    editor: {
      type: Object,
      required: true,
    },
    extension: {
      type: Object,
      required: true,
    },
    deleteNode: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      isVertical: false,
    };
  },
  computed: {
    isEditMode() {
      return this.extension.options.mode === "edit";
    },
    isNotary() {
      return this.node.attrs.participant_type === "notary";
    },
    isEDS() {
      return this.signatureData?.algorithm === "SHA256";
    },
    isSigned() {
      return this.signatureData?.status === SignatureStatus.SIGNED;
    },
    signatureData() {
      // eslint-disable-next-line no-unused-expressions
      this.node.attrs.signatureVersion;
      const signatures = this.editor?.storage?.signature?.signatures || [];
      if (this.isNotary) {
        return signatures.find((s) => s.participant_uuid === null);
      }
      return signatures.find(
        (s) =>
          s.participant?.participant_type_code ===
          this.node.attrs.participant_type_code
      );
    },
    signatureLabel() {
      if (this.isNotary) {
        const n = this.signatureData?.notary_info;
        if (n) {
          const name = (n?.fio || "").trim();
          const iin = n.identifier || "";
          if (name && iin) return `${name}, ${this.$t("ИИН")} ${iin}`;
          return name;
        }
      }
      const p = this.signatureData?.participant;
      const name = p
        ? `${p.last_name || ""} ${p.first_name || ""} ${
            p.middle_name || ""
          }`.trim()
        : this.node.attrs.participant_name || "";
      const iin = p?.identifier || "";
      if (name && iin) return `${name}, ${this.$t("ИИН")} ${iin}`;
      return name;
    },
    signatureFileUrl() {
      let url = this.signatureData?.signature_file_url || "";
      if (!url) return "";
      if (url.startsWith("http")) return url;
      if (url.startsWith("/app/")) url = url.slice(4);
      const domain = window.__CONFIG__.VUE_APP_BASE_DOMAIN;
      if (url.startsWith("/")) return domain + url;
      return domain + "/media/" + url;
    },

    qrChunks() {
      const base64 = this.signatureData?.signature_base64;
      if (!base64) return [];
      const chunkSize = Math.ceil(base64.length / QR_COUNT);
      const chunks = [];
      for (let i = 0; i < QR_COUNT; i++) {
        chunks.push(base64.slice(i * chunkSize, (i + 1) * chunkSize));
      }
      return chunks;
    },
  },
  methods: {
    onImageLoad(e) {
      const img = e.target;
      this.isVertical = img.naturalHeight > img.naturalWidth;
    },
  },
};
</script>

<style scoped>
.signature-qr-row {
  @apply flex gap-1 items-center;
}

.signature-hand-box {
  @apply w-[200px] inline-flex items-center justify-center p-0.5 bg-white;
}

.signature-hand-img {
  @apply h-[100px] object-contain;
  image-rendering: -webkit-optimize-contrast;
}

.signature-hand-img--rotated {
  @apply h-[100px] -rotate-90;
}

.signature-label {
  @apply mt-0.5 block;
}
</style>
